import { motion } from "framer-motion";
import { Phone, Snowflake, Wind, Wrench, ScanLine, Thermometer, ShieldCheck } from "lucide-react";
import { IMAGES, PHONE_DISPLAY, PHONE_TEL } from "../data/site";

const fade = (i = 0) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.15 },
  transition: { duration: 0.55, delay: i * 0.07 },
});

const FEATURES = [
  {
    icon: Thermometer,
    title: "Re-gassing",
    desc: "Air conditioning gone warm? Sam re-gasses your system on the spot and checks it's actually holding before he drives off.",
  },
  {
    icon: ScanLine,
    title: "Leak detection",
    desc: "If the gas is going somewhere, Sam finds where — UV leak detection means the fix targets the fault, not just the symptom.",
  },
  {
    icon: Wrench,
    title: "Repairs",
    desc: "Compressors, condensers, hoses and the wiring in between — air conditioning faults repaired properly, not patched.",
  },
  {
    icon: Wind,
    title: "Servicing & maintenance",
    desc: "Keep the system healthy year-round so it's not a 38-degree emergency in the middle of a Queensland summer.",
  },
];

export const Aircon = () => (
  <section
    id="aircon"
    data-testid="aircon-section"
    className="py-20 sm:py-24 lg:py-32 border-y border-line bg-surface/40 relative overflow-hidden"
  >
    <div
      aria-hidden="true"
      className="pointer-events-none absolute top-0 right-0 w-[520px] h-[520px] rounded-full opacity-20"
      style={{ background: "radial-gradient(circle, rgba(56,189,248,0.35) 0%, transparent 65%)" }}
    />
    <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
      <motion.p
        {...fade()}
        className="font-mono text-[11px] uppercase tracking-[0.28em] text-sky-400 font-semibold mb-4"
      >
        On site — auto air conditioning
      </motion.p>
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12 lg:mb-16">
        <motion.h2
          {...fade(1)}
          data-testid="aircon-section-heading"
          className="font-display font-bold uppercase tracking-tight text-slate-50 text-3xl sm:text-4xl lg:text-5xl leading-[0.95] max-w-2xl"
        >
          Keeping the Coast cool.
        </motion.h2>
        <motion.p {...fade(2)} className="text-slate-400 max-w-sm text-base leading-relaxed">
          Auto air conditioning re-gassing, repairs, servicing and maintenance —
          mobile, year-round. Not just a summer thing.
        </motion.p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 items-start">
        <motion.div {...fade(1)} className="lg:col-span-5 relative">
          <div className="relative rounded-2xl border border-line overflow-hidden">
            <img
              src={IMAGES.airconUv}
              alt="UV light revealing a refrigerant leak on an air conditioning condenser"
              className="w-full h-[300px] sm:h-[380px] object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-transparent" />
            <p className="absolute bottom-4 left-5 right-5 font-mono text-[10px] uppercase tracking-[0.22em] text-slate-300">
              UV leak detection — find the fault, fix it once
            </p>
          </div>
        </motion.div>

        <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5">
          {FEATURES.map((f, i) => (
            <motion.div
              key={f.title}
              {...fade(i)}
              data-testid={`aircon-card-${i + 1}`}
              className="rounded-2xl border border-line bg-surface p-6 lg:p-7 h-full transition-all duration-300 hover:border-sky-400/50 hover:-translate-y-1 hover:shadow-[0_20px_60px_rgba(56,189,248,0.1)]"
            >
              <span className="inline-flex items-center justify-center w-11 h-11 rounded-lg bg-sky-400/10 border border-sky-400/25 text-sky-400">
                <f.icon size={20} />
              </span>
              <h3 className="font-display font-bold uppercase text-xl text-slate-50 mt-5 tracking-tight">
                {f.title}
              </h3>
              <p className="text-slate-400 mt-3 leading-relaxed text-[15px]">{f.desc}</p>
            </motion.div>
          ))}

          <motion.a
            {...fade(4)}
            href={PHONE_TEL}
            data-testid="aircon-cta-call"
            className="sm:col-span-2 inline-flex items-center justify-center gap-3 h-14 rounded-full border border-sky-400/40 bg-sky-400/10 hover:bg-sky-400/20 text-sky-300 font-semibold text-base transition-colors"
          >
            <Phone size={18} />
            Aircon playing up? Call Sam — {PHONE_DISPLAY}
          </motion.a>

          <motion.p
            {...fade(5)}
            data-testid="aircon-licence"
            className="sm:col-span-2 flex items-center justify-center gap-2.5 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400"
          >
            <ShieldCheck size={15} className="text-sky-400" />
            Arctick refrigerant licence — AU54074
          </motion.p>
        </div>
      </div>
    </div>
  </section>
);
